import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchRecipes, addMealFromSpoonacular } from '../services/api';

const AddMealModal = ({ show, handleClose, day, mealType, onMealAdded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');
  const [usedCategorySearch, setUsedCategorySearch] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Clear search results when modal is closed
  useEffect(() => {
    if (!show) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedRecipe(null);
      setError('');
      setUsedCategorySearch(false);
    }
  }, [show]);

  // Define handleSearch with useCallback to avoid creating new function on each render
  const handleSearch = useCallback(async () => {
    if (searchQuery.trim().length < 2) return;
    
    setIsSearching(true);
    setError('');
    setSearchResults([]);
    setUsedCategorySearch(false);
    
    try {
      console.log('Searching for:', searchQuery, mealType);
      
      const response = await searchRecipes(searchQuery, mealType);
      console.log('Search response:', response);
      
      // The response should be an array directly
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          setSearchResults(response.data);
          console.log('Setting results:', response.data.length);
          
          // Check for metadata in headers that indicates category search was used
          if (response.headers && response.headers['x-search-method'] === 'category') {
            setUsedCategorySearch(true);
          } else if (response.data.length > 0 && searchQuery.toLowerCase() !== response.data[0].title.toLowerCase()) {
            // If the top result doesn't match the query closely, we probably used categorical search
            setUsedCategorySearch(true);
          }
        } else {
          console.error('Unexpected response format:', response.data);
          setError('Unexpected response format from the server.');
        }
      } else {
        console.error('Empty response from server');
        setError('No results found. Try another search term.');
      }
    } catch (error) {
      console.error('Error searching recipes:', error);
      setError('Error connecting to recipe service. Please try again later.');
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, mealType]); // Add dependencies here

  // Handle search query changes with debounce
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch();
    }, 1000); // 1 second debounce
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, handleSearch]); // Add handleSearch as dependency

  const handleAddRecipe = async (recipe) => {
    setSelectedRecipe(recipe);
    setIsAdding(true);
    setError('');
    
    try {
      // If the recipe has a db_id, it's already in our database
      const payload = {
        recipe_id: recipe.id,
        meal_type: mealType
      };
      
      // If we have a database ID, include it
      if (recipe.db_id) {
        payload.db_id = recipe.db_id;
      }
      
      const response = await addMealFromSpoonacular(payload);
      if (response && response.data) {
        onMealAdded(response.data.id, day, mealType);
        handleClose();
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error adding recipe:', error);
      setError('Error adding recipe to meal plan. Please try again.');
    } finally {
      setIsAdding(false);
      setSelectedRecipe(null);
    }
  };

  const handleAddCustomMeal = async (mealName) => {
    setIsAdding(true);
    setError('');
    
    try {
      // Use the API to create a custom meal
      const payload = {
        name: mealName,
        meal_type: mealType,
        is_custom: true
      };
      
      const response = await addMealFromSpoonacular(payload);
      
      if (response && response.data) {
        onMealAdded(response.data.id, day, mealType);
        handleClose();
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating custom meal:', error);
      setError('Error creating custom meal. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Add {mealType.charAt(0).toUpperCase() + mealType.slice(1)} for {day.charAt(0).toUpperCase() + day.slice(1)}
              </h5>
              <button type="button" className="btn-close" onClick={handleClose}></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="searchQuery" className="form-label">Search for recipes</label>
                <input
                  type="text"
                  className="form-control"
                  id="searchQuery"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type at least 2 characters to search..."
                />
                {isSearching && <p className="text-muted mt-2">Searching...</p>}
                {error && <p className="text-danger mt-2">{error}</p>}
                <small className="text-muted d-block mt-2">
                  Enter a recipe name like "pasta", "chicken", etc.
                </small>
              </div>
              
              {usedCategorySearch && searchResults.length > 0 && (
                <div className="alert alert-info" role="alert">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  No exact match found for "{searchQuery}". Showing related recipes instead.
                </div>
              )}
              
              {searchResults.length > 0 ? (
                <div className="row">
                  {searchResults.map(recipe => (
                    <div key={recipe.id} className="col-md-6 mb-3">
                      <div className="card h-100">
                        {recipe.image && (
                          <img
                            src={recipe.image}
                            className="card-img-top"
                            alt={recipe.title}
                            style={{ height: '200px', objectFit: 'cover' }}
                          />
                        )}
                        <div className="card-body">
                          <h5 className="card-title">{recipe.title}</h5>
                          {recipe.readyInMinutes && (
                            <p className="card-text">
                              <small className="text-muted">
                                Ready in {recipe.readyInMinutes} minutes
                              </small>
                            </p>
                          )}
                          {recipe.db_id && (
                            <span className="badge bg-success mb-2">From database</span>
                          )}
                          <button
                            className="btn btn-primary mt-2"
                            onClick={() => handleAddRecipe(recipe)}
                            disabled={isAdding && selectedRecipe?.id === recipe.id}
                          >
                            {isAdding && selectedRecipe?.id === recipe.id
                              ? 'Adding...'
                              : 'Add to Meal Plan'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                searchQuery.length >= 2 && !isSearching && !error && (
                  <div className="text-center mt-4">
                    <p>No results found for "{searchQuery}".</p>
                    <button 
                      className="btn btn-primary mt-2"
                      onClick={() => handleAddCustomMeal(searchQuery)}
                      disabled={isAdding}
                    >
                      {isAdding ? 'Creating...' : `Create Custom Meal: "${searchQuery}"`}
                    </button>
                  </div>
                )
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
              >
                Close
              </button>
              {usedCategorySearch && searchResults.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => handleAddCustomMeal(searchQuery)}
                  disabled={isAdding}
                >
                  {isAdding ? 'Creating...' : `Create Custom Meal: "${searchQuery}"`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMealModal; 